/**
 * Role-wise Chart of Accounts access.
 *
 * Settings row (Settings table):
 *   key: role_wise_coa_access
 *   valueBool: true  → restrict COA lists by role permissions
 *   campusID: 0 (or empty) → school-wide / all campuses
 *   campusID: N           → override for one campus (used only if no school-wide row)
 *
 * Role COA allow-list (coaAccountIDs) is on the role — same for the user everywhere.
 *
 * Role forms JSON (child for route "accounts"):
 *   coaAccountIDs: [] or [0]  → all accounts
 *   coaAccountIDs: [12, 45, 67]  → only these account IDs (+ parents on tree views)
 */

export const ROLE_COA_ACCESS_SETTING_KEY = "role_wise_coa_access";

/** True when campus setting role_wise_coa_access is on (Vuex or local page flag). */
export function isCoaAccessFeatureActive(store, vm) {
  if (store?.state?.roleCoaAccessEnabled === true) return true;
  if (vm?.roleCoaAccessEnabled === true) return true;
  return false;
}

export function normalizeSettingsList(response) {
  if (Array.isArray(response)) return response;
  if (response?.status === "success" && Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

/** Prefer school-wide setting row, then logged-in campus, then any matching row. */
export function pickRoleCoaAccessSetting(settingsList, cId) {
  if (!Array.isArray(settingsList)) return null;
  const rows = settingsList.filter((el) => el.key === ROLE_COA_ACCESS_SETTING_KEY);
  if (!rows.length) return null;

  const isGlobal = (el) => {
    const id = el.campusID;
    return id == null || id === "" || Number(id) === 0;
  };

  const global = rows.find(isGlobal);
  if (global) return global;

  if (cId != null && cId !== "") {
    const campus = rows.find((el) => Number(el.campusID) === Number(cId));
    if (campus) return campus;
  }

  return rows[0];
}

export function commitRoleCoaAccessEnabled(store, setting) {
  const enabled = isRoleCoaAccessEnabled(setting);
  if (store?.commit) store.commit("setRoleCoaAccessEnabled", enabled);
  return enabled;
}

export function commitRoleCoaAccessFromSettingsResponse(store, response, cId) {
  const list = normalizeSettingsList(response);
  const setting = pickRoleCoaAccessSetting(
    list,
    cId ?? store?.state?.userData?.cId
  );
  return commitRoleCoaAccessEnabled(store, setting);
}

export function isRoleCoaAccessEnabled(setting) {
  if (!setting) return false;
  const v = setting.valueBool;
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const t = v.toLowerCase().trim();
    return t === "true" || t === "1" || t === "yes";
  }
  return false;
}

/** @returns {number[]|null} null = no restriction (all accounts) */
export function getRoleAllowedCoaIds(accountRights) {
  if (!accountRights || !Array.isArray(accountRights.coaAccountIDs)) {
    return null;
  }
  const ids = accountRights.coaAccountIDs
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id) && id > 0);
  if (!ids.length) return null;
  return ids;
}

export function extractAccountsCoaFromForms(forms) {
  if (!forms) return null;
  try {
    const menus = typeof forms === "string" ? JSON.parse(forms) : forms;
    if (!Array.isArray(menus)) return null;
    for (const group of menus) {
      for (const ch of group.children || []) {
        if (ch.route === "accounts" && Array.isArray(ch.coaAccountIDs)) {
          return ch.coaAccountIDs;
        }
      }
    }
  } catch (e) {
    /* ignore parse errors */
  }
  return null;
}

/** Persist COA allow-list from user/role forms (parse before menu filtering). */
export function syncAccountCoaRestrictionsToStore(store, forms) {
  if (!store?.commit) return;
  const ids = extractAccountsCoaFromForms(forms);
  if (getRoleAllowedCoaIds({ coaAccountIDs: ids })) {
    store.commit("setAccountCoaRestrictions", ids);
  } else {
    store.commit("setAccountCoaRestrictions", null);
  }
}

/** Resolve COA restrictions from rights, menu, or logged-in user forms. */
export function getRoleCoaRightsFromStore(store) {
  const fromPersisted = store?.state?.accountCoaRestrictions;
  if (getRoleAllowedCoaIds({ coaAccountIDs: fromPersisted })) {
    return { coaAccountIDs: fromPersisted };
  }

  const rightsAccounts = store?.state?.rights?.accounts;
  if (getRoleAllowedCoaIds(rightsAccounts)) {
    return rightsAccounts;
  }

  let menuIds = null;
  (store?.state?.menu || []).forEach((group) => {
    (group.children || []).forEach((ch) => {
      if (ch.route === "accounts" && Array.isArray(ch.coaAccountIDs)) {
        menuIds = ch.coaAccountIDs;
      }
    });
  });
  if (getRoleAllowedCoaIds({ coaAccountIDs: menuIds })) {
    return { coaAccountIDs: menuIds };
  }

  const userFormsIds = extractAccountsCoaFromForms(
    store?.state?.loggedUser?.forms
  );
  if (getRoleAllowedCoaIds({ coaAccountIDs: userFormsIds })) {
    return { coaAccountIDs: userFormsIds };
  }

  return rightsAccounts || { coaAccountIDs: [0] };
}

function accountIdField(item) {
  return Number(item.id != null ? item.id : item.accountID);
}

function parentIdField(item) {
  const p = item.parentHead != null ? item.parentHead : item.parentID;
  const n = Number(p);
  return Number.isNaN(n) ? 0 : n;
}

/** Include selected accounts and ancestor heads so trees stay valid. */
export function expandCoaIdsWithParents(accounts, allowedIds) {
  if (!allowedIds || !allowedIds.length || !Array.isArray(accounts)) {
    return null;
  }
  const byId = new Map();
  accounts.forEach((row) => {
    const id = accountIdField(row);
    if (!Number.isNaN(id)) byId.set(id, row);
  });

  const expanded = new Set(allowedIds);
  allowedIds.forEach((id) => {
    let cur = byId.get(id);
    let guard = 0;
    while (cur && guard < 50) {
      const parentId = parentIdField(cur);
      if (!parentId) break;
      expanded.add(parentId);
      cur = byId.get(parentId);
      guard += 1;
    }
  });
  return expanded;
}

/** When a parent head is allowed, include all descendant accounts (reports / flat lists). */
export function expandCoaIdsWithDescendants(accounts, allowedSet) {
  if (!allowedSet || !allowedSet.size || !Array.isArray(accounts)) {
    return allowedSet;
  }
  const childrenByParent = new Map();
  accounts.forEach((row) => {
    const id = accountIdField(row);
    const parentId = parentIdField(row);
    if (Number.isNaN(id)) return;
    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
    childrenByParent.get(parentId).push(id);
  });

  const visit = (parentId) => {
    (childrenByParent.get(parentId) || []).forEach((childId) => {
      if (!allowedSet.has(childId)) {
        allowedSet.add(childId);
        visit(childId);
      }
    });
  };

  [...allowedSet].forEach((id) => visit(id));
  return allowedSet;
}

/** Filter to allowed account IDs only (no parent/descendant expansion). */
export function filterCoaListByRole(accounts, accountRights) {
  if (!Array.isArray(accounts)) return accounts;
  const allowedIds = getRoleAllowedCoaIds(accountRights);
  if (!allowedIds) return accounts;
  const allowed = new Set(allowedIds);
  return accounts.filter((row) => allowed.has(accountIdField(row)));
}

/** Tree views: allowed IDs + ancestor heads (+ optional descendants). */
export function filterCoaListByRoleTree(
  accounts,
  accountRights,
  { includeDescendants = false } = {}
) {
  if (!Array.isArray(accounts)) return accounts;
  const allowedIds = getRoleAllowedCoaIds(accountRights);
  if (!allowedIds) return accounts;

  let expanded = expandCoaIdsWithParents(accounts, allowedIds);
  if (!expanded) return accounts;
  if (includeDescendants) {
    expanded = expandCoaIdsWithDescendants(accounts, expanded);
  }

  return accounts.filter((row) => expanded.has(accountIdField(row)));
}

export function normalizeRoleCoaSelection(selectedIds) {
  if (!Array.isArray(selectedIds) || !selectedIds.length) return [0];
  if (selectedIds.at(-1) === 0) return [0];
  return selectedIds.filter((id) => Number(id) !== 0);
}

export function normalizeAccountsLoadResponse(response) {
  if (response && response.status === "success" && Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response)) return response;
  return [];
}

/** Load setting flag onto vm.roleCoaAccessEnabled */
export async function initRoleCoaAccess(vm) {
  if (!vm || typeof vm.post !== "function") return;
  try {
    const res = await vm.post({
      url:
        vm.$store.state.domain +
        "Settings/getSettingsAll?db=" +
        vm.$store.state.userData.db,
      body: [ROLE_COA_ACCESS_SETTING_KEY],
      context: vm,
      token: vm.$store.state.userData.token,
      subjects: true,
    });
    const enabled = commitRoleCoaAccessFromSettingsResponse(
      vm.$store,
      res,
      vm.$store.state.userData.cId
    );
    vm.roleCoaAccessEnabled = enabled;
  } catch (e) {
    commitRoleCoaAccessEnabled(vm.$store, null);
    vm.roleCoaAccessEnabled = false;
  }
}

/** Apply COA filter only when setting is on and role lists specific account IDs. */
export function applyRoleCoaAccessFilter(vm, list) {
  if (!Array.isArray(list)) return list;
  if (!isCoaAccessFeatureActive(vm.$store, vm)) return list;
  return filterCoaListByRole(list, getRoleCoaRightsFromStore(vm.$store));
}

function journalDetailAccountId(line) {
  if (!line) return 0;
  const id = line.accountID != null ? line.accountID : line.accountId;
  const n = Number(id);
  return Number.isNaN(n) ? 0 : n;
}

/** Voucher list: hide entries that use any COA line outside the allow-list. */
export function journalEntryAllowedByCoaRights(item, accountRights) {
  const allowedIds = getRoleAllowedCoaIds(accountRights);
  if (!allowedIds) return true;
  const allowed = new Set(allowedIds);
  const details = item?.details;
  if (!Array.isArray(details) || !details.length) return true;
  return details.every((line) => {
    const id = journalDetailAccountId(line);
    if (!id) return true;
    return allowed.has(id);
  });
}

export function filterJournalEntriesByCoaRights(list, store, vm) {
  if (!Array.isArray(list)) return list;
  if (!isCoaAccessFeatureActive(store, vm)) return list;
  const rights = getRoleCoaRightsFromStore(store);
  if (!getRoleAllowedCoaIds(rights)) return list;
  return list.filter((item) => journalEntryAllowedByCoaRights(item, rights));
}

/** Optional API query when backend supports filtering by allowed COA ids. */
export function buildCoaAccountIdsQueryParam(store, vm) {
  if (!isCoaAccessFeatureActive(store, vm)) return "";
  const ids = getRoleAllowedCoaIds(getRoleCoaRightsFromStore(store));
  if (!ids?.length) return "";
  return (
    "&accountIDs=" + ids.map((id) => encodeURIComponent(String(id))).join(",")
  );
}

/** Collection / cash accounts linked to COA via accountID (or similar). */
export function filterCollectionAccountsByRole(list, accountRights) {
  if (!Array.isArray(list)) return list;
  const allowedIds = getRoleAllowedCoaIds(accountRights);
  if (!allowedIds) return list;

  const allowed = new Set(allowedIds);
  return list.filter((row) => {
    const linked =
      row.accountID != null && row.accountID !== ""
        ? Number(row.accountID)
        : row.valueNumber != null && row.valueNumber !== ""
        ? Number(row.valueNumber)
        : null;
    if (linked == null || Number.isNaN(linked) || linked === 0) {
      return true;
    }
    return allowed.has(linked);
  });
}
